import React from 'react';
import { formatAmount } from '../../constants';
import { BUTTON_CONFIG } from './types';

interface ResolutionOptionsProps {
  resolution: any;
  exc: any;
  isResolved: boolean;
  auditData: any;
  userCanApprove: boolean;
  blockedReason: string | null;
  escalationTarget: string | null;
  executingOption: string | null;
  investigatingOption: string | null;
  investigationNotes: string;
  investigationSubmitted: Record<string, boolean>;
  statusKey: string;
  onExecuteResolution: (optionId: string, action: string, optionLabel?: string, optionDesc?: string) => void;
  onEscalateToLevel: (level: string) => void;
  onEscalate: () => void;
  onStartInvestigation: (optionId: string) => void;
  onApproveAfterInvestigation: (optionId: string, optionLabel?: string, optionDesc?: string) => void;
  onSetInvestigatingOption: (optionId: string | null) => void;
  onSetInvestigationNotes: (notes: string) => void;
}

export default function ResolutionOptions({
  resolution, exc, isResolved, auditData,
  userCanApprove, blockedReason, escalationTarget,
  executingOption, investigatingOption, investigationNotes,
  investigationSubmitted, statusKey,
  onExecuteResolution, onEscalateToLevel, onEscalate,
  onStartInvestigation, onApproveAfterInvestigation,
  onSetInvestigatingOption, onSetInvestigationNotes,
}: ResolutionOptionsProps) {
  if (!resolution?.options?.length) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>RESOLUTION OPTIONS</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {resolution.options.map((opt: any) => {
          const isRecommended = opt.recommended;
          const isExecuting = executingOption === opt.id;
          const isEscalation = opt.escalate_to || /escalat|refer to|investigate further|review by (manager|director|senior|cfo|supervisor)/i.test(`${opt.label || ''} ${opt.description || ''}`);
          const actionType: string = opt.action_type || (isEscalation ? 'ESCALATE' : 'APPROVE');
          const btnCfg = BUTTON_CONFIG[actionType] || BUTTON_CONFIG.APPROVE;

          const needsAuthority = ['APPROVE', 'WRITE_OFF', 'ACCRUE'].includes(actionType);
          const auditBlocked = auditData && !auditData.approve_allowed;
          const isBlocked = needsAuthority && (!userCanApprove || auditBlocked);

          return (
            <div key={opt.id} style={{
              padding: 14, borderRadius: 10,
              background: isRecommended ? 'rgba(0,212,170,0.05)' : 'rgba(0,0,0,0.02)',
              border: isRecommended ? '1px solid rgba(0,212,170,0.2)' : '1px solid var(--bg-border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: isRecommended ? 'var(--accent-good)' : 'var(--text-dim)' }}>{opt.id}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{opt.label}</span>
                  {isRecommended && <span style={{ fontSize: 9, padding: '2px 8px', background: 'rgba(0,212,170,0.1)', color: 'var(--accent-good)', borderRadius: 4, fontWeight: 700 }}>RECOMMENDED</span>}
                  <span style={{ fontSize: 8, padding: '2px 6px', background: btnCfg.bg, color: btnCfg.color, borderRadius: 3, fontWeight: 700, letterSpacing: 0.5 }}>{actionType}</span>
                </div>
                {opt.confidence != null && (
                  <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: opt.confidence >= 0.8 ? 'var(--accent-good)' : opt.confidence >= 0.5 ? 'var(--accent-warning)' : 'var(--text-dim)' }}>
                    {(opt.confidence * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', lineHeight: 1.6, marginBottom: 8 }}>{opt.description}</div>

              {/* Validation Warnings */}
              {opt.validation_warnings?.length > 0 && (
                <div style={{ padding: 8, background: 'rgba(251,191,36,0.08)', borderRadius: 4, marginBottom: 8, fontSize: 9 }}>
                  {opt.validation_warnings.map((w: string, i: number) => (
                    <div key={i} style={{ color: 'var(--warning)' }}>Warning: {w}</div>
                  ))}
                </div>
              )}

              {/* Journal Entries */}
              {opt.journal_entries?.length > 0 && (
                <div style={{ padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 6, marginBottom: 10 }}>
                  <div style={{ fontSize: 9, color: 'var(--text-dimmer)', fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>JOURNAL ENTRIES</div>
                  {opt.journal_entries[0]?.type === 'NO_ADJUSTMENT' ? (
                    <div style={{ borderLeft: '3px solid var(--navy-700)', paddingLeft: 12, color: 'var(--text-muted)', fontSize: 10, lineHeight: 1.6 }}>
                      {opt.journal_entries[0].reason || 'No financial adjustment required'}
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                          <th style={{ textAlign: 'left', padding: '4px 6px', color: 'var(--text-dimmer)', fontWeight: 700, fontSize: 9 }}>Account</th>
                          <th style={{ textAlign: 'left', padding: '4px 6px', color: 'var(--text-dimmer)', fontWeight: 700, fontSize: 9 }}>Name</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', color: 'var(--text-dimmer)', fontWeight: 700, fontSize: 9 }}>Debit</th>
                          <th style={{ textAlign: 'right', padding: '4px 6px', color: 'var(--text-dimmer)', fontWeight: 700, fontSize: 9 }}>Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {opt.journal_entries.map((je: any, j: number) => {
                          if (je.type === 'NO_ADJUSTMENT') return null;
                          // Mode 2 paired format
                          if (je.debit_account || je.credit_account) {
                            const amt = Number(je.amount || 0);
                            return (
                              <React.Fragment key={j}>
                                {je.debit_account && (
                                  <tr>
                                    <td style={{ padding: '3px 6px', color: 'var(--navy-700)' }}>{je.debit_account}</td>
                                    <td style={{ padding: '3px 6px', color: 'var(--text-dim)' }}>{je.description || '\u2014'}</td>
                                    <td className="mono" style={{ textAlign: 'right', padding: '3px 6px', color: 'var(--text-primary)', fontWeight: 700 }}>${amt.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    <td style={{ textAlign: 'right', padding: '3px 6px', color: 'var(--text-dimmer)' }}>{'\u2014'}</td>
                                  </tr>
                                )}
                                {je.credit_account && (
                                  <tr>
                                    <td style={{ padding: '3px 6px', color: 'var(--positive)' }}>{je.credit_account}</td>
                                    <td style={{ padding: '3px 6px', color: 'var(--text-dim)' }}>{je.description || '\u2014'}</td>
                                    <td style={{ textAlign: 'right', padding: '3px 6px', color: 'var(--text-dimmer)' }}>{'\u2014'}</td>
                                    <td className="mono" style={{ textAlign: 'right', padding: '3px 6px', color: 'var(--text-primary)', fontWeight: 700 }}>${amt.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          }
                          // Mode 1 single-sided format
                          const dr = Number(je.debit || 0);
                          const cr = Number(je.credit || 0);
                          return (
                            <tr key={j}>
                              <td style={{ padding: '3px 6px', color: dr > 0 ? 'var(--navy-700)' : 'var(--positive)' }}>{je.account || '\u2014'}</td>
                              <td style={{ padding: '3px 6px', color: 'var(--text-dim)' }}>{je.account_name || '\u2014'}</td>
                              <td className="mono" style={{ textAlign: 'right', padding: '3px 6px', color: 'var(--text-primary)', fontWeight: 700 }}>
                                {dr > 0 ? `$${dr.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '\u2014'}
                              </td>
                              <td className="mono" style={{ textAlign: 'right', padding: '3px 6px', color: 'var(--text-primary)', fontWeight: 700 }}>
                                {cr > 0 ? `$${cr.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '\u2014'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {!isResolved && (() => {
                const isUnderInvestigation = investigationSubmitted[opt.id] || statusKey === 'under_investigation';
                const showInvestigateForm = investigatingOption === opt.id && !isUnderInvestigation;

                // INVESTIGATE action_type
                if (actionType === 'INVESTIGATE') {
                  return (
                    <div style={{ marginTop: 8 }}>
                      {!showInvestigateForm && !isUnderInvestigation && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            onClick={() => { onSetInvestigatingOption(opt.id); onSetInvestigationNotes(''); }}
                            disabled={!!executingOption}
                            style={{ padding: '8px 16px', background: BUTTON_CONFIG.INVESTIGATE.bg, border: BUTTON_CONFIG.INVESTIGATE.border, borderRadius: 6, color: BUTTON_CONFIG.INVESTIGATE.color, fontWeight: 700, fontSize: 10, cursor: 'pointer' }}
                          >
                            INVESTIGATE
                          </button>
                          <button
                            onClick={() => escalationTarget ? onEscalateToLevel(escalationTarget) : onEscalate()}
                            disabled={!!executingOption}
                            style={{ padding: '8px 16px', background: BUTTON_CONFIG.ESCALATE.bg, border: BUTTON_CONFIG.ESCALATE.border, borderRadius: 6, color: BUTTON_CONFIG.ESCALATE.color, fontWeight: 700, fontSize: 10, cursor: 'pointer' }}
                          >
                            ESCALATE
                          </button>
                        </div>
                      )}

                      {showInvestigateForm && (
                        <div style={{ padding: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 8 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--warning)', marginBottom: 6, letterSpacing: 0.5 }}>INVESTIGATION NOTES (SOX REQUIRED)</div>
                          <textarea
                            value={investigationNotes}
                            onChange={(e) => onSetInvestigationNotes(e.target.value)}
                            placeholder="Document your investigation findings... (min 10 characters)"
                            rows={3}
                            style={{ width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 11, resize: 'vertical', fontFamily: 'inherit' }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                            <button
                              onClick={() => onSetInvestigatingOption(null)}
                              style={{ padding: '6px 12px', background: 'none', border: '1px solid var(--bg-border)', borderRadius: 4, color: 'var(--text-dim)', fontSize: 10, cursor: 'pointer' }}
                            >
                              CANCEL
                            </button>
                            <button
                              onClick={() => onStartInvestigation(opt.id)}
                              disabled={!!executingOption || investigationNotes.trim().length < 10}
                              style={{ padding: '6px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, color: 'var(--warning)', fontWeight: 700, fontSize: 10, cursor: investigationNotes.trim().length < 10 ? 'not-allowed' : 'pointer', opacity: investigationNotes.trim().length < 10 ? 0.5 : 1 }}
                            >
                              {isExecuting ? 'SUBMITTING...' : 'SUBMIT INVESTIGATION'}
                            </button>
                          </div>
                        </div>
                      )}

                      {isUnderInvestigation && (
                        <div style={{ padding: 10, background: 'rgba(0,212,170,0.04)', border: '1px solid rgba(0,212,170,0.2)', borderRadius: 8 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--accent-good)', marginBottom: 6, letterSpacing: 0.5 }}>INVESTIGATION COMPLETE — SELECT ACTION</div>
                          {isBlocked && (
                            <div style={{ fontSize: 9, color: 'var(--warning)', marginBottom: 8 }}>
                              {blockedReason || `Amount exceeds your authority`}
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button
                              onClick={() => onApproveAfterInvestigation(opt.id, opt.label, opt.description)}
                              disabled={!!executingOption || isBlocked}
                              title={isBlocked ? (blockedReason || 'Exceeds your authority') : 'Approve with documented justification'}
                              style={{ padding: '8px 16px', background: isBlocked ? 'rgba(0,0,0,0.05)' : 'rgba(0,212,170,0.15)', border: 'none', borderRadius: 6, color: isBlocked ? 'var(--text-dimmer)' : '#fff', fontWeight: 700, fontSize: 10, cursor: isBlocked ? 'not-allowed' : 'pointer', opacity: isBlocked ? 0.5 : 1 }}
                            >
                              {isExecuting ? 'PROCESSING...' : 'APPROVE AFTER INVESTIGATION'}
                            </button>
                            <button
                              onClick={() => escalationTarget ? onEscalateToLevel(escalationTarget) : onEscalate()}
                              disabled={!!executingOption}
                              style={{ padding: '8px 16px', background: BUTTON_CONFIG.ESCALATE.bg, border: BUTTON_CONFIG.ESCALATE.border, borderRadius: 6, color: BUTTON_CONFIG.ESCALATE.color, fontWeight: 700, fontSize: 10, cursor: 'pointer' }}
                            >
                              ESCALATE INSTEAD
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // ESCALATE action_type
                if (actionType === 'ESCALATE') {
                  return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                      {escalationTarget ? (
                        <button
                          onClick={() => onEscalateToLevel(escalationTarget)}
                          disabled={!!executingOption}
                          style={{ padding: '8px 16px', background: BUTTON_CONFIG.ESCALATE.bg, border: BUTTON_CONFIG.ESCALATE.border, borderRadius: 6, color: BUTTON_CONFIG.ESCALATE.color, fontWeight: 700, fontSize: 10, cursor: 'pointer' }}
                        >
                          {isExecuting ? 'PROCESSING...' : `ESCALATE TO ${escalationTarget}`}
                        </button>
                      ) : (
                        <span style={{ fontSize: 9, color: 'var(--text-dimmer)', fontStyle: 'italic' }}>Already at maximum authority level</span>
                      )}
                    </div>
                  );
                }

                // APPROVE / WRITE_OFF / ACCRUE
                const excAmount = Number(exc.amount || 0);
                return (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    {isBlocked && (
                      <span style={{ fontSize: 9, color: auditBlocked ? 'var(--negative)' : 'var(--warning)' }}>
                        {auditBlocked ? `Blocked: ${auditData?.critical_count ?? 0} critical audit finding(s)` : (blockedReason || `Amount exceeds your authority`)}
                      </span>
                    )}
                    <button
                      onClick={() => onExecuteResolution(opt.id, 'approved', opt.label, opt.description)}
                      disabled={!!executingOption || isBlocked}
                      title={isBlocked ? (blockedReason || `Exception amount $${excAmount.toLocaleString()} exceeds your authority`) : ''}
                      style={{
                        padding: '8px 16px',
                        background: isBlocked ? 'rgba(0,0,0,0.05)' : btnCfg.bg,
                        border: btnCfg.border, borderRadius: 6,
                        color: isBlocked ? 'var(--text-dimmer)' : btnCfg.color,
                        fontWeight: 700, fontSize: 10,
                        cursor: isBlocked ? 'not-allowed' : executingOption ? 'wait' : 'pointer',
                        opacity: isBlocked ? 0.5 : 1,
                      }}
                    >
                      {isExecuting ? 'PROCESSING...' : btnCfg.label}
                    </button>
                    {isBlocked && (
                      <button
                        onClick={onEscalate}
                        disabled={!!executingOption}
                        style={{ padding: '8px 16px', background: BUTTON_CONFIG.ESCALATE.bg, border: BUTTON_CONFIG.ESCALATE.border, borderRadius: 6, color: BUTTON_CONFIG.ESCALATE.color, fontWeight: 700, fontSize: 10, cursor: 'pointer' }}
                      >
                        ESCALATE INSTEAD
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
